import { Button, Collapse, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, IconButton, Stack, Switch, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ResponsiveDialog from "../core/ResponsiveDialog";
import { makeStyles } from "tss-react/mui";
import { useDevice } from "../../../providers/DeviceProvider";
import { useStation } from "../../../providers/StationProvider";
import DynamicCard from "../display/DynamicCard";
import { Close as CloseIcon, InfoOutlined as InfoIcon } from "@mui/icons-material";
import DynamicAccordion from "../display/DynamicAccordion";
import MultiModalInput from "./MultiModalInput";
import { getEncodedImage, MakeEmptyTask } from "../../../common/mission";
import { Location, useAuth, useEntities } from "@emberly/zenith-client";
import axios from "axios";
import { FormatLocation, GeoCodeProperties } from "../../../common/maphelpers";
import FieldPickerTable from "./FieldPickerTable";
import { MissionEnums, MissionEnumsLists } from "../../../common/constants";
import MapboxImage from "../core/MapboxImage";
import { Masonry } from "@mui/lab";
import { formatText } from "../../../common/helpers";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      position: "relative",
      width: "685px",
      maxWidth: "100%",
      height: "auto",
      maxHeight: "95vh",
      overflow: "hidden"
    },
    rootMobile: {
      position: "absolute",
      width: "100%",
      height: "calc(100% - 48px)"
    },
    info: {
      "& a": {
        color: "inherit"
      }
    },
    card: {
      width: "100%",
      minHeight: "240px",
      height: "100%",
      "& .img-container": {
        maxHeight: "20vh",
      }
    },
    locationImage: {
      width: "100%",
      height: "auto",
      "& img": {
        borderRadius: theme.spacing(1),
      }
    },
    switch: {
      display: "flex",
      justifyContent: "space-between",
      marginLeft: theme.spacing(0)
    },
    useRegisterDataDescription: {
      marginLeft: theme.spacing(0),
      "& span": {
        color: theme.palette.info.main
      }
    },
    textBlue: {
      color: theme.palette.info.main,
      wordBreak: "break-word"
    },
    textSecondary: {
      color: theme.palette.text.secondary,
      wordBreak: "break-word"
    }
  })
);


export default function CreateMissionDialog(props) {
  const { task } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { getAccessTokenSilently } = useAuth();
  const { entities: causes } = useEntities("Cause");
  const { screenWidth } = useDevice();
  const { station } = useStation();

  const [createFromExistingData, setCreateFromExistingData] = useState(false);
  const [inputExpanded, setInputExpanded] = useState(true);
  const [outputExpanded, setOutputExpanded] = useState(false);

  const [inputData, setInputData] = useState({ text: "", images: [] });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(false);
  const [rateLimit, setRateLimit] = useState(false);

  const [missionData, setMissionData] = useState(null);
  const [hasOwner, setHasOwner] = useState(null);
  const [hasRegisterData, setHasRegisterData] = useState(false);
  const [useRegisterData, setUseRegisterData] = useState(true);

  const [detailFields, setDetailFields] = useState([]);
  const [objectFields, setObjectFields] = useState([]);
  const [requesterFields, setRequesterFields] = useState([]);
  const [taskFields, setTaskFields] = useState([]);


  const onUseRegisterData = useCallback((ev) => {
    const v = ev.target.checked;
    setUseRegisterData(v)
  }, []);

  const onGenerate = useCallback(async () => {
    try {
      setError(false);
      setRateLimit(false);
      setWorking(true);
      setHasRegisterData(false);

      const res = await axios.post("/api/mission/ai/extract", {
        text: inputData.text,
        images: inputData.images.map(img => getEncodedImage(img)),
        causes: causes.map(t => t.title),
        stationName: station.legalName
      }, {
        headers: { Authorization: `Bearer ${await getAccessTokenSilently()}` },
      });

      if (!!res.data && res.status === 200) {
        const m = res.data;

        const foundReg = !!m.vehicleRegistration;

        const ownerName = foundReg ? formatText(" ", m.vehicleRegistration?.ownerData?.firstName, m.vehicleRegistration?.ownerData?.middleName, m.vehicleRegistration?.ownerData?.lastName) : "";
        const requesterName = m.requesterName || ownerName || "";
        const ownerIsNotRequester = !!ownerName && !!m.requesterName && ownerName?.toLocaleLowerCase() !== requesterName?.toLocaleLowerCase();

        setHasOwner(ownerIsNotRequester);
        setHasRegisterData(foundReg);
        setUseRegisterData(foundReg)

        const geocode = await GeoCodeProperties({
          requesterAddress: m.requesterAddress,
          ownerAddress: m?.vehicleRegistration?.ownerData?.address,
          missionLocation: m.missionLocation,
          missionTransportTo: m.missionTransportTo
        }, true, true, station?.location?.coordinates?.length === 2 ? station.location.coordinates.join(",") : null);

        let missionParsed = {
          ...m,
          missionType: parseMissionType(m.missionType),
          targetType: parseTargetType(m.targetType),
          requesterAddress: ownerIsNotRequester ? geocode.requesterAddress : (geocode.requesterAddress || geocode.ownerAddress),
          missionLocation: !geocode.missionLocation && m?.missionLocation?.coordinates?.length === 2 ? new Location(m.missionLocation) : geocode.missionLocation,
          missionTransportTo: !geocode.missionTransportTo && m?.missionTransportTo?.coordinates?.length === 2 ? new Location(m.missionTransportTo) : geocode.missionTransportTo,
          ownerAddress: geocode.ownerAddress,
          ownerName,
          requesterName,
        };

        setMissionData(missionParsed);
        setInputExpanded(false);
        setOutputExpanded(true);
      } else {

        if (res.status === 429) {
          setRateLimit(true);
        }

        // TODO error
        setError(true);
      }

    } catch (err) {
      console.log(err);
      setError(true);
      setInputExpanded(true);
      // TODO error
    } finally {
      setWorking(false);
    }

  }, [inputData, getAccessTokenSilently, causes, station]);


  const createMission = useCallback(async () => {

    const fields = [
      ...detailFields,
      ...objectFields,
      ...requesterFields,
      ...taskFields
    ];

    const includedData = {};

    const reg = missionData.vehicleRegistration;
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      const propertyValue = missionData[f.name];

      if (f.value && propertyValue !== "null") {

        if (useRegisterData) {
          includedData[f.name] = reg[f.name] || missionData[f.name];
        } else {
          includedData[f.name] = missionData[f.name];
        }

      }
    }

    const cause = !!includedData.cause ? causes.find(t => t.title === includedData.cause) : null;

    const targetData = {
      make: includedData.make || "",
      model: includedData.model || "",
      registration: includedData.registration || ""
    }

    const targetType = includedData.targetType || MissionEnums.TargetType.Car;

    const ownerIsNotRequester = useRegisterData && !!includedData.ownerName && !!includedData.requesterName && includedData.ownerName?.toLocaleLowerCase() !== includedData.requesterName?.toLocaleLowerCase();

    const entityData = {
      state: MissionEnums.State.Draft,
      details: {
        description: includedData?.description || "",
        tags: [],
        type: includedData.missionType || MissionEnums.Type.Towing,
        cause: !!cause ? { id: cause.id, name: cause.title } : undefined,
      },
      target: {
        description: "",
        type: targetType,
        carDetails: {
          color: includedData.color || "",
          driveTrain: includedData.driveTrain,
          fuel: includedData.fuel,
          chassisNumber: includedData.chassisNumber || "",
          weightInKG: includedData.weightInKG,
          year: includedData.year,
          service: {},
          make: "",
          model: "",
          registration: "",
          ...(targetType === MissionEnums.TargetType.Car ? targetData : {})
        },
        boatDetails: {
          make: "",
          model: "",
          registration: "",
          ...(targetType === MissionEnums.TargetType.Boat ? targetData : {})
        }
      },
      actors: {
        ownerIsNotRequester: ownerIsNotRequester,
        hasContactPerson: false,
        owner: {
          name: (ownerIsNotRequester ? includedData.ownerName : includedData.requesterName) || "",
          email: (ownerIsNotRequester ? "" : includedData.requesterEmail) || "",
          phoneNumber: (ownerIsNotRequester ? "" : includedData.requesterPhoneNumber) || "",
          location: ownerIsNotRequester ? (includedData.ownerAddress || new Location({})).getData() : (includedData.requesterAddress || new Location({})).getData(),
          organizationNumber: "",
          billing: {
            country: "",
            address: "",
            address2: "",
            email: "",
            postNumber: "",
            area: ""
          },
        },
        requester: {
          name: ownerIsNotRequester ? includedData.requesterName : "",
          email: "",
          phoneNumber: "",
          location: ownerIsNotRequester ? (includedData.requesterAddress || new Location({})).getData() : new Location({}).getData(),
          organizationNumber: "",
          billing: {
            country: "",
            address: "",
            address2: "",
            email: "",
            postNumber: "",
            area: ""
          },
        },
        contactPerson: {
          name: "",
          email: "",
          phoneNumber: "",
          description: ""
        },
        contract: {
          deductible: {
            currency: 0,
            value: ""
          },
          description: "",
          reference: "",
          insurance: {
            insurer: "",
            state: 0,
            type: 0
          }
        }
      },
      salvageTasks: [
        MakeEmptyTask(
          1,
          null,
          [
            includedData.missionLocation?.getData(),
            includedData.missionTransportTo?.getData(),
          ].filter(t => !!t),
          includedData.missionLocationDescription || ""
        )
      ]
    };

    return entityData;
  }, [detailFields, objectFields, requesterFields, taskFields, missionData, causes, useRegisterData]);

  const onCreateMissionThenOpen = useCallback(async () => {
    const m = await createMission();
    task.complete(m);
  }, [createMission, task]);

  return (
    <ResponsiveDialog
      open
      onClose={() => task.throw("cancelled")}
      maxWidth={createFromExistingData ? "md" : "sm"}
      fullWidth
    >
      <DialogTitle id="alert-dialog-title">
        {createFromExistingData ? t("mission:createDialog:generateTitle") : t("mission:createDialog:title")}
        <IconButton onClick={() => task.throw("cancelled")} color="primary" sx={{ float: "right", marginRight: -1 }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>

        <Collapse in={!createFromExistingData}>
          <Grid container justifyContent="center" alignItems="center" spacing={2}>

            <Grid item xs={12} md={6}>
              <DynamicCard
                className={classes.card}
                img="/assets/images/cards/new_blank_mission.jpg"
                title={t("mission:createDialog:createEmptyDraft")}
                description={t("mission:createDialog:createEmptyDraftDescription")}
                onClick={() => task.complete({})}
                delay={350}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DynamicCard
                className={classes.card}
                img="/assets/images/cards/create_mission_existing.jpg"
                title={t("mission:createDialog:createFromExisting")}
                description={t("mission:createDialog:createFromExistingDescription")}
                onClick={() => setCreateFromExistingData(true)}
                delay={350}
              />
            </Grid>

          </Grid>
        </Collapse>

        <Collapse in={createFromExistingData}>
          <Grid container justifyContent="center" alignItems="center" spacing={2}>

            <Grid item xs={12}>
              <DynamicAccordion
                title={t("mission:createDialog:step1")}
                expanded={inputExpanded}
                onExpand={setInputExpanded}
                defaultExpanded
              >
                <MultiModalInput
                  placeholder={t("mission:createDialog:placeholderInputText")}
                  imageLimit={3}
                  error={error ? (rateLimit ? t("mission:createDialog:rateLimit") : t("mission:createDialog:error")) : undefined}
                  textLimitText={t("mission:createDialog:textLimitText")}
                  imageLimitText={t("mission:createDialog:imageLimitText")}
                  imageSizeLimitText={t("mission:createDialog:imageSizeLimitText")}
                  onChange={setInputData}
                  disabled={working}
                  working={working}
                  onConfirm={onGenerate}
                />
              </DynamicAccordion>
            </Grid>


            <Grid item xs={12}>
              <DynamicAccordion
                title={t("mission:createDialog:step2")}
                expanded={outputExpanded}
                onExpand={setOutputExpanded}
              >

                <Masonry sequential columns={screenWidth < 900 ? 1 : 2} spacing={2} sx={{ width: "auto" }}>

                  <FieldPickerTable
                    working={working}
                    title={t("mission:createDialog:details")}
                    data={missionData}
                    onChange={setDetailFields}
                    fields={useMemo(() => [
                      {
                        name: "missionType",
                        title: t("mission:cards:details:type"),
                        display: (data) => t(`mission:enums:type:${MissionEnumsLists.Type[data?.missionType || 0]}`),
                      },
                      {
                        name: "cause",
                        title: t("mission:cards:details:cause"),
                        display: (data) => data?.cause || "-",
                        hidden: data => !data.cause
                      },
                      {
                        name: "description",
                        title: t("mission:cards:details:description"),
                        display: (data) => data?.description || "-",
                        hidden: data => !data.description
                      },
                    ], [t])}
                  />

                  <FieldPickerTable
                    working={working}
                    title={t("mission:createDialog:object")}
                    data={missionData}
                    onChange={setObjectFields}
                    fields={useMemo(() => [
                      {
                        name: "targetType",
                        title: t("mission:cards:target:type"),
                        display: (data) => t(`mission:enums:targetType:${MissionEnumsLists.TargetType[data?.targetType || 0]}`),
                      },
                      {
                        name: "registration",
                        title: t("mission:cards:target:registration"),
                        display: (data) => data?.registration || "-",
                        hidden: data => !data.registration
                      },
                      {
                        name: "chassisNumber",
                        title: t("mission:cards:target:chassisNumberShort"),
                        display: (data) => useRegisterData ? <RenderOption text={data?.vehicleRegistration?.chassisNumber} /> : "",
                        hidden: data => !useRegisterData
                      },
                      {
                        name: "make",
                        title: t("mission:cards:target:make"),
                        display: (data) => useRegisterData ? <RenderOption text={data?.vehicleRegistration?.make} alternative={data?.make} /> : (data?.make || "-"),
                        hidden: data => !data.make && !useRegisterData || useRegisterData && !data?.vehicleRegistration?.make
                      },
                      {
                        name: "model",
                        title: t("mission:cards:target:model"),
                        display: (data) => useRegisterData ? <RenderOption text={data?.vehicleRegistration?.model} alternative={data?.model} /> : data?.model || "-",
                        hidden: data => !data.model && !useRegisterData || useRegisterData && !data?.vehicleRegistration?.model
                      },
                      {
                        name: "color",
                        title: t("mission:cards:target:color"),
                        display: (data) => <RenderOption text={data?.vehicleRegistration?.color} />,
                        hidden: data => !data?.vehicleRegistration?.color || !useRegisterData
                      },
                      {
                        name: "year",
                        title: t("mission:cards:target:year"),
                        display: (data) => <RenderOption text={data?.vehicleRegistration?.year} />,
                        hidden: data => !data?.vehicleRegistration?.year || !useRegisterData
                      },
                      {
                        name: "weightInKG",
                        title: t("mission:cards:target:weight"),
                        display: (data) => <RenderOption text={data?.vehicleRegistration?.weightInKG} />,
                        hidden: data => !data?.vehicleRegistration?.weightInKG || !useRegisterData
                      },
                      {
                        name: "driveTrain",
                        title: t("mission:cards:target:driveTrain"),
                        display: (data) => <RenderOption text={t(`mission:enums:driveTrain:${MissionEnumsLists.DriveTrain[data?.vehicleRegistration?.driveTrain || 0]}`)} />,
                        hidden: data => !data?.vehicleRegistration?.driveTrain || !useRegisterData
                      },
                      {
                        name: "fuel",
                        title: t("mission:cards:target:fuel"),
                        display: (data) => <RenderOption text={t(`mission:enums:fuelType:${MissionEnumsLists.FuelType[data?.vehicleRegistration?.fuel || 0]}`)} />,
                        hidden: data => !data?.vehicleRegistration?.fuel || !useRegisterData
                      },
                      {
                        hidden: () => !hasRegisterData,
                        renderRow: (data) => (
                          <Stack>
                            <Divider />

                            <FormControlLabel
                              checked={useRegisterData}
                              onChange={onUseRegisterData}
                              slotProps={{ typography: { variant: "subtitle2" } }}
                              className={classes.switch}
                              label={t("mission:createDialog:useRegisterData")}
                              labelPlacement="start"
                              control={<Switch />}
                              disabled={working}
                            />

                            <Typography variant="caption" color="textSecondary" className={classes.useRegisterDataDescription}>
                              {useRegisterData ? (
                                <>
                                  <span>{t("mission:createDialog:useRegisterDataDescriptionBlue")}</span>&nbsp;
                                  {t("mission:createDialog:useRegisterDataDescription")}
                                </>
                              ) : t("mission:createDialog:useRegisterDataOff")}
                            </Typography>

                          </Stack>
                        )
                      }
                    ], [t, hasRegisterData, useRegisterData, onUseRegisterData, working, classes])}
                  />

                  <FieldPickerTable
                    working={working}
                    title={t("mission:createDialog:requester")}
                    data={missionData}
                    onChange={setRequesterFields}
                    fields={useMemo(() => [
                      {
                        name: "requesterName",
                        title: t("common:name"),
                        display: (data) => data?.requesterName || "-",
                      },
                      {
                        name: "requesterAddress",
                        title: t("entity:location:address"),
                        display: (data) => data?.requesterAddress ? FormatLocation(data.requesterAddress) : "-",
                      },
                      {
                        name: "requesterEmail",
                        title: t("common:email"),
                        display: (data) => data?.requesterEmail || "-",
                        hidden: data => !data.requesterEmail
                      },
                      {
                        name: "requesterPhoneNumber",
                        title: t("common:phoneNumber"),
                        display: (data) => data?.requesterPhoneNumber || "-",
                        hidden: data => !data.requesterPhoneNumber
                      },
                      {
                        header: t("mission:createDialog:owner"),
                        hidden: () => !hasOwner || !useRegisterData
                      },
                      {
                        name: "ownerName",
                        title: t("common:name"),
                        display: (data) => <RenderOption text={data?.ownerName} />,
                        hidden: () => !hasOwner || !useRegisterData
                      },
                      {
                        name: "ownerAddress",
                        title: t("entity:location:address"),
                        display: (data) => <RenderOption text={data?.ownerAddress ? FormatLocation(data.ownerAddress) : "-"} />,
                        hidden: () => !hasOwner || !useRegisterData
                      },
                    ], [t, hasOwner, useRegisterData])}
                  />

                  <FieldPickerTable
                    working={working}
                    title={t("mission:createDialog:task")}
                    data={missionData}
                    onChange={setTaskFields}
                    fields={useMemo(() => [
                      {
                        name: "missionLocationDescription",
                        title: t("mission:cards:tasks:salvageTask:description"),
                        display: (data) => data?.missionLocationDescription || "-",
                      },
                      {
                        name: "missionLocation",
                        title: t("mission:cards:tasks:salvageTask:missionLocation"),
                        render: (data) => !!data?.missionLocation ? (<RenderLocationCell location={data.missionLocation} />) : "-",
                        hidden: data => !data.missionLocation
                      },
                      {
                        name: "missionTransportTo",
                        title: t("mission:cards:tasks:salvageTask:transportTo"),
                        render: (data) => !!data?.missionTransportTo ? (<RenderLocationCell location={data.missionTransportTo} />) : "-",
                        hidden: data => !data.missionTransportTo
                      },
                    ], [t])}
                  />



                </Masonry>

                <Grid container spacing={1} justifyContent="flex-end">
                  <Grid item>
                    <Button variant="contained" onClick={onCreateMissionThenOpen} disabled={!missionData || working}>{t("mission:createDialog:finishCreatingMission")}</Button>
                  </Grid>
                </Grid>


              </DynamicAccordion>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="text.disabled" className={classes.info} textAlign={screenWidth < 900 ? "center" : "left"}>
                {screenWidth < 900 ? null : <InfoIcon color="inherit" fontSize="inherit" sx={{ display: "inline", marginBottom: "-2px", marginRight: 0.5 }} />}
                {t("mission:createDialog:info")} <a href="https://help.getassist.app/nb/articles/11570468" target="_blank">{t("mission:createDialog:infoLink")}</a>
              </Typography>
            </Grid>

          </Grid>
        </Collapse>


      </DialogContent>
    </ResponsiveDialog>
  );
}


function RenderOption(props) {
  const { classes } = useStyles();
  const { text, alternative } = props;

  return (
    <>
      <span className={classes.textBlue}>{text || alternative || "-"}</span>
      {!!text && !!alternative ? <span className={classes.textSecondary}>&nbsp;({alternative})</span> : null}
    </>
  )
}


function RenderLocationCell(props) {
  const { location } = props;
  const { classes } = useStyles();

  const geojson = useMemo(() => ({
    "type": "FeatureCollection",
    "features": [
      location.getGeoJson()
    ]
  }), [location]);

  return (
    <Stack spacing={1}>
      <Typography variant="body2">{FormatLocation(location) || "-"}</Typography>
      <MapboxImage geojson={geojson} zoomLevel={14} width={500} height={250} className={classes.locationImage} />
    </Stack>
  );
}


export function parseMissionType(type) {

  switch (type) {
    case "TRANSPORT":
      return MissionEnums.Type.Towing;
    case "SALVAGE":
      return MissionEnums.Type.Salvage;
    case "OTHER":
      return MissionEnums.Type.Other;
    default:
      return MissionEnums.Type.Unknown;
  }
}



export function parseTargetType(type) {
  switch (type) {
    case "CAR":
      return MissionEnums.TargetType.Car;
    case "BOAT":
      return MissionEnums.TargetType.Boat;
    case "OTHER":
      return MissionEnums.TargetType.Other;
    default:
      return MissionEnums.TargetType.Unknown;
  }
}
